/**
 * First-login detection — per user, per browser.
 *
 * "First login" means the first time this browser has seen an authenticated
 * session for this userId. The flag lives in localStorage so it survives
 * page reloads but is scoped to the device (intentional — a user on a new
 * device IS a first login for that device).
 *
 * Called from FirstLoginUpgradeHero on mount so the card marks itself seen
 * the instant it renders. Back-navigation won't re-show it.
 */

import { useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

const flagKey = (userId: string) => `pw_first_login_seen_${userId}`;

/** Returns true if this is the first authenticated session for userId on this browser. */
export function isFirstLogin(userId: string): boolean {
  if (!userId) return false;
  return localStorage.getItem(flagKey(userId)) === null;
}

/** Write the seen-flag. Call once from the FirstLoginUpgradeHero on mount. */
export function markFirstLoginSeen(userId: string): void {
  if (!userId) return;
  localStorage.setItem(flagKey(userId), String(Date.now()));
}

/**
 * Session-stable "is this a first-login session?" — captures isFirstLogin on
 * first read for a given userId and freezes it for the rest of the React app's
 * lifetime. Once FirstLoginUpgradeHero writes the seen-flag, the underlying
 * isFirstLogin() flips to false; consumers of THIS hook keep seeing true for
 * the duration of the session so they can suppress competing prompts. Next
 * app mount (tab close + reopen, or refresh) the user is no longer first-login
 * and normal behavior resumes.
 */
export function useIsFirstLoginSession(): boolean {
  const { getUserId } = useAuth();
  const userId = getUserId() ?? '';
  const captured = useRef<{ userId: string; value: boolean } | null>(null);
  if (!userId) return false;
  if (captured.current?.userId !== userId) {
    captured.current = { userId, value: isFirstLogin(userId) };
  }
  return captured.current.value;
}
